import { Hex } from 'viem';
import {
  CommentCacheItem,
  CommentsV2CacheItem,
  db,
  IdentityModuleCache,
  KeyStoreCache,
  PortalCollection,
  UserCache,
  UserIdentity,
} from './db';
import { IDdoc, TGunUserNode } from '@/types';
import { BEACON_FACTORY_ADDRESS } from '@/utils/identity-utils/constants';
import { WorkerInstance } from '@/utils/worker-utils/worker-instance';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { isNullish } from '@/utils/gun-utils';
import { ARCH_VERSION } from '@/utils/constants';

export const getPortalByOwner = async (address: Hex) =>
  await db.portals.where('owner').equals(address).first();

export const addPortal = async (portal: PortalCollection) => {
  await db.portals.put(portal);
};

export const updateSession = async (walletAddress: Hex, signedMessage: Hex) => {
  const expiresAt = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days
  await db.sessions.put({ address: walletAddress, signedMessage, expiresAt });
};

export const updateCommentSession = async (
  username: string,
  password: string
) => {
  await db.commentAccount.put({ username, password });
};

export const getSession = async (walletAddress: Hex) =>
  await db.sessions.where('address').equals(walletAddress).first();

export const getCommentAccountSession = async (username: string) =>
  await db.commentAccount.where('username').equals(username).first();

export const getDocById = async (id: string) => {
  return await db.ddocs.where('ddocId').equals(id).first();
};

export const getDdocsOnIndexedDb = async () => {
  return await db.ddocs.toArray();
};

export const deleteLocalDdocById = async (id: string) => {
  return await db.ddocs.where('ddocId').equals(id).delete();
};

export const getExistingDocOnIndexeddb = async (ddocId: string) => {
  if (!ddocId) return null;
  return (await db.ddocs.get({ ddocId })) || null;
};

export const addDdocToIndexdb = async (ddoc: IDdoc) => {
  return await db.ddocs.add(ddoc);
};

export const getExistingDocsByPortalAddress = async (portalAddress: Hex) => {
  const existingDocs = await db.ddocs
    .filter((doc) => doc.portalAddress === portalAddress)
    .toArray();
  return existingDocs;
};

export const updateExistingDocContentById = async (id: string, data: IDdoc) => {
  return await db.ddocs
    .where('ddocId')
    .equals(id)
    .modify({ ...data });
};

export const clearSession = async (walletAddress: Hex) =>
  await db.sessions.where('address').equals(walletAddress).delete();

export const getUserDDocCache = async (walletAddress: string) => {
  const cache = await db.userCache
    .where('address')
    .equals(walletAddress)
    .toArray();
  return cache;
};

export const getDeviceLocalCache = async () => {
  return await db.userCache.toArray();
};

export const addToUserDDocCache = async (
  walletAddress: string,
  data: UserCache
) => {
  const existingCache = await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, data.ddocId])
    .first();

  // For new entries, simply add the data
  if (!existingCache) {
    return addNewUserCacheItem({ ...data, address: walletAddress });
  }

  // Skip if existing file is newer
  if (Number(existingCache.onChainFileId) > Number(data.onChainFileId)) {
    return;
  }

  // Collect all updates in one object
  const updates: Partial<UserCache> = {};

  // Basic field updates
  if (
    data.portalAddress.toLowerCase() !==
    existingCache?.portalAddress?.toLowerCase()
  ) {
    updates.portalAddress = data.portalAddress;
  }

  // Timestamp updates
  if (
    data.createdAt &&
    (!existingCache.createdAt || existingCache.createdAt !== data.createdAt)
  ) {
    updates.createdAt = data.createdAt;
  }
  if (
    data.last_updated &&
    data.last_updated > (existingCache.last_updated ?? 0)
  ) {
    updates.last_updated = data.last_updated;
  }

  // Version and status updates
  if (data.archVersion && existingCache.archVersion !== data.archVersion) {
    updates.archVersion = data.archVersion;
    updates.migrationStatus = data.migrationStatus;
  }

  // Key and lock updates
  if (data.linkKey && existingCache.linkKey !== data.linkKey) {
    updates.linkKey = data.linkKey;
    updates.linkKeyNonce = data.linkKeyNonce;
  }

  if (data.encryptedTitle && !existingCache.title) {
    updates.encryptedTitle = data.encryptedTitle;
    updates.needToDecryptTitle = true;
  }

  // File ID update

  if (
    !isNullish(data.onChainFileId) &&
    (isNullish(existingCache.onChainFileId) ||
      Number(existingCache.onChainFileId) < Number(data.onChainFileId))
  ) {
    updates.onChainFileId = data.onChainFileId;

    // Also update in indexDB if needed
    const existingDoc = await getExistingDocOnIndexeddb(data.ddocId);
    if (existingDoc) {
      await db.ddocs.put({
        ...existingDoc,
        onChainFileId: data.onChainFileId,
      });
    }
  }

  // Apply updates if any changes were found
  if (Object.keys(updates).length > 0) {
    await db.userCache
      .where('[address+ddocId]')
      .equals([walletAddress, data.ddocId])
      .modify(updates);
  }
};

const addNewUserCacheItem = async (data: UserCache) => {
  const cacheItem = { ...data };
  if (cacheItem.ownerLock?.lockedLinkKey) {
    try {
      const privateKey = KeyStoreInstance.getDecryptionKey('owner');
      const decryptedKey = await WorkerInstance.decryptRSA(
        cacheItem.ownerLock.lockedLinkKey,
        privateKey
      );

      cacheItem.linkKey = new TextDecoder().decode(decryptedKey);
    } catch (error) {
      console.error('Error decrypting link key', error);
    }
  }

  await db.userCache.add({ ...cacheItem, address: data.address });
};

export const addToUserDDocCacheForGun = async (
  walletAddress: string,
  data: UserCache,
  userNode: TGunUserNode
) => {
  const existingCache = await getCachedDoc(walletAddress, data.ddocId);

  if (!existingCache) {
    await db.userCache.add({ ...data, address: walletAddress });
    return;
  }

  if (existingCache) {
    // If the document is already in the cache, and the arch version is the same, we don't need to add/update it to the cache
    if (existingCache.archVersion === ARCH_VERSION) return;

    // Prepare a single update object
    const updates: Partial<UserCache> = {};

    // Add any new keys from data that don't exist in existingCache
    for (const key in data) {
      const typedKey = key as keyof UserCache;
      if (!(typedKey in existingCache) && data[typedKey] !== undefined) {
        // @ts-ignore
        updates[typedKey] = data[typedKey];
      }
    }

    if (!existingCache.title && data.title) {
      updates.title = data.title;
      updates.needToDecryptTitle = false;
    }

    if (data.createdAt && data.createdAt > (existingCache.createdAt ?? 0)) {
      updates.createdAt = data.createdAt;
    }

    if (
      data.last_updated &&
      data.last_updated > (existingCache.last_updated ?? 0)
    ) {
      updates.last_updated = data.last_updated;
    }

    if (data.linkKey && !existingCache.linkKey) {
      const decryptedLinkKey = (
        await WorkerInstance.decryptDdocItem(
          { linkKey: data.linkKey },
          // @ts-ignore
          userNode._.sea
        )
      ).linkKey;
      updates.linkKey = decryptedLinkKey;
    }

    if (data.linkKeyNonce && !existingCache.linkKeyNonce) {
      const decryptedLinkKeyNonce = (
        await WorkerInstance.decryptDdocItem(
          { linkKeyNonce: data.linkKeyNonce },
          // @ts-ignore
          userNode._.sea
        )
      ).linkKeyNonce;
      updates.linkKeyNonce = decryptedLinkKeyNonce;
    }

    if (
      data.onChainFileId &&
      existingCache.onChainFileId !== data.onChainFileId
    ) {
      updates.onChainFileId = data.onChainFileId;
    }

    if (data.portalAddress && !existingCache.portalAddress) {
      updates.portalAddress = data.portalAddress;
    }

    // Only perform the update if there are changes
    if (Object.keys(updates).length > 0) {
      await db.userCache
        .where('[address+ddocId]')
        .equals([walletAddress, data.ddocId])
        .modify(updates);
    }

    return;
  }
};

export const updateTitleInCache = async (
  walletAddress: string,
  ddocId: string,
  title: string
) => {
  if (title === 'Untitled' || title === '') {
    await db.userCache
      .where('[address+ddocId]')
      .equals([walletAddress, ddocId])
      .modify({ needToDecryptTitle: false });
    return;
  }

  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .modify({ title, needToDecryptTitle: false });
};

export const updateLastUpdatedInCache = async (
  walletAddress: string,
  ddocId: string,
  last_updated: number
) => {
  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .modify({ last_updated });
};

export const deleteItemFromUserCache = async (
  walletAddress: string,
  ddocId: string
) => {
  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .delete();
};

export const getCachedDoc = async (
  walletAddress: string,
  ddocId: string
): Promise<UserCache | undefined> => {
  return await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .first();
};

export const addCommentToCache = async (comment: CommentCacheItem) => {
  if (!comment.ddocId || !comment.commentId) return;
  const commentExists = await db.commentCache
    .where('[ddocId+commentId]')
    .equals([comment.ddocId, comment.commentId])
    .first();
  if (commentExists) return;

  await db.commentCache.put(comment);
};

export const addCommentToCacheV2 = async (comment: CommentsV2CacheItem) => {
  if (!comment.ddocId || !comment.id) return;

  const commentExists = await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([comment.ddocId, comment.id])
    .first();
  if (commentExists) return;

  await db.commentsV2Cache.put(comment);
};

export const getCommentItemById = async (ddocId: string, id: string) => {
  const commentExists = await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .first();

  return commentExists;
};

export const getAllCommentsByDdocId = async (ddocId: string) => {
  if (!ddocId) return [];
  return await db.commentCache.where('ddocId').equals(ddocId).toArray();
};

export const getAllCommentsByDdocIdV2 = async (ddocId: string) => {
  if (!ddocId) return [];
  return await db.commentsV2Cache.where('ddocId').equals(ddocId).toArray();
};

export const bulkUpdateLastReadTimestamp = async (
  ddocId: string,
  timestamp: number
) => {
  await db.commentCache
    .where('ddocId')
    .equals(ddocId)
    .and((comment) => comment.lastReadTimestamp === null)
    .modify({ lastReadTimestamp: timestamp });
};

export const bulkUpdateLastReadTimestampV2 = async (
  ddocId: string,
  timestamp: number
) => {
  await db.commentsV2Cache
    .where('ddocId')
    .equals(ddocId)
    .and((comment) => comment.lastReadTimestamp === null)
    .modify({ lastReadTimestamp: timestamp });
};

export const updateCommentIndexV2 = async (
  ddocId: string,
  id: string,
  commentIndex: number
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .modify({ commentIndex: commentIndex });
};
export const updateCommentContentHashV2 = async (
  ddocId: string,
  id: string,
  hash: string
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .modify({ contentHash: hash });
};

export const updateReplyCommentIndexV2 = async (
  ddocId: string,
  commentId: string,
  replyId: string,
  newCommentIndex: number
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, commentId])
    .modify((comment) => {
      if (comment.replies) {
        const reply = comment.replies.find((r) => r.id === replyId);
        if (reply) {
          reply.commentIndex = newCommentIndex;
        }
      }
    });
};

export const updateRepliesOnCommentV2 = async (
  ddocId: string,
  id: string,
  replies: CommentsV2CacheItem['replies']
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .modify({ replies: replies });
};

export const updateResolveOnCommentV2 = async (
  ddocId: string,
  id: string,
  resolved: CommentsV2CacheItem['resolved']
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .modify({ resolved: resolved });
};

export const updateDeleteOnCommentV2 = async (
  ddocId: string,
  id: string,
  deleted: CommentsV2CacheItem['deleted']
) => {
  const comment = await getCommentItemById(ddocId, id);
  if (deleted && !comment?.deleted) {
    // do not update delete field if comment has already been deleted locally
    await db.commentsV2Cache
      .where('[ddocId+id]')
      .equals([ddocId, id])
      .modify({ deleted: deleted });
  }
};

export const updateTimestampOnCommentV2 = async (
  ddocId: string,
  id: string,
  createdAt: CommentsV2CacheItem['createdAt']
) => {
  if (!createdAt) return;
  const comment = await getCommentItemById(ddocId, id);
  if (
    !comment?.createdAt ||
    createdAt?.getTime() > comment?.createdAt?.getTime()
  ) {
    await db.commentsV2Cache
      .where('[ddocId+id]')
      .equals([ddocId, id])
      .modify({ createdAt: createdAt });
  }
};

export const modifyUserCacheData = async (
  ddocId: string,
  walletAddress: string,
  data: Partial<UserCache>
) => {
  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .modify((item) => {
      // This will both update existing properties and add new ones
      Object.assign(item, data);
    });
};

export const updateHasLinkKeyChangedOnCache = async (
  walletAddress: string,
  ddocId: string,
  hasLinkKeyChanged: boolean
) => {
  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .modify({ hasLinkKeyChanged: hasLinkKeyChanged });
};

export const updateCommentTxHashOnCommentV2 = async (
  ddocId: string,
  id: string,
  txHash: CommentsV2CacheItem['txHash']
) => {
  if (!txHash) return;
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, id])
    .modify({ txHash: txHash });
};

export const updateReplyTxHashV2 = async (
  ddocId: string,
  commentId: string,
  replyId: string,
  txHash: string
) => {
  await db.commentsV2Cache
    .where('[ddocId+id]')
    .equals([ddocId, commentId])
    .modify((comment) => {
      if (comment.replies) {
        const reply = comment.replies.find((r) => r.id === replyId);
        if (reply) {
          reply.txHash = txHash;
        }
      }
    });
};

export const checkAndUpdateCacheItem = async (
  ddocItem: IDdoc,
  walletAddress: string
) => {
  if (!ddocItem) return;
  if (!ddocItem.ddocId || !walletAddress) return;
  const existingCacheItem = await getCachedDoc(walletAddress, ddocItem.ddocId);
  if (!existingCacheItem) return;
  const updatesToCache: Partial<UserCache> = {};

  if (
    ddocItem.publishStatus &&
    existingCacheItem.publishStatus !== ddocItem.publishStatus
  )
    updatesToCache.publishStatus = ddocItem.publishStatus;
  if (ddocItem.linkKey && existingCacheItem.linkKey !== ddocItem.linkKey)
    updatesToCache.linkKey = ddocItem.linkKey;
  if (
    ddocItem.linkKeyNonce &&
    existingCacheItem.linkKeyNonce !== ddocItem.linkKeyNonce
  )
    updatesToCache.linkKeyNonce = ddocItem.linkKeyNonce;
  if (
    !isNullish(ddocItem.onChainFileId) &&
    existingCacheItem.onChainFileId !== ddocItem.onChainFileId
  )
    updatesToCache.onChainFileId = ddocItem.onChainFileId;

  if (Object.keys(updatesToCache).length > 0) {
    await modifyUserCacheData(ddocItem.ddocId, walletAddress, updatesToCache);
  }
};

export const getUserIdentity = async (walletAddress: Hex) => {
  return await db.userIdentity
    .where('[walletAddress+factoryAddress]')
    .equals([walletAddress, BEACON_FACTORY_ADDRESS])
    .first();
};

export const modifyUserIdentity = async (
  walletAddress: Hex,
  data: Partial<UserIdentity>
) => {
  await db.userIdentity
    .where('[walletAddress+factoryAddress]')
    .equals([walletAddress, BEACON_FACTORY_ADDRESS])
    .modify(data);
};

export const updateUserIdentitySignedMessage = async (
  walletAddress: Hex,
  signedMessage: Hex
) => {
  const existingIdentity = await getUserIdentity(walletAddress);
  if (!existingIdentity)
    await addUserSignedMessage(walletAddress, signedMessage);
  else await modifyUserIdentity(walletAddress, { signedMessage });
};

export const getIdentitySignedMessage = async (walletAddress: Hex) => {
  return await db.userIdentity
    .where('[walletAddress+factoryAddress]')
    .equals([walletAddress, BEACON_FACTORY_ADDRESS])
    .first();
};

const addUserSignedMessage = async (walletAddress: Hex, signedMessage: Hex) => {
  await db.userIdentity.add({
    walletAddress,
    factoryAddress: BEACON_FACTORY_ADDRESS,
    signedMessage,
    identityModuleAddress: null,
  });
};

export const getKeyStoreCache = async (portalAddress: Hex, hash: string) => {
  return await db.keyStoreCache
    .where('[portalAddress+hash]')
    .equals([portalAddress, hash])
    .first();
};

export const addKeyStoreCache = async (keyStoreCache: KeyStoreCache) => {
  await db.keyStoreCache.add(keyStoreCache);
};

export const addIdentityModuleCache = async (
  identityModuleCache: IdentityModuleCache
) => {
  await db.identityModuleCache.add(identityModuleCache);
};

export const getIdentityModuleCache = async (moduleAddress: Hex) => {
  return await db.identityModuleCache
    .where('moduleAddress')
    .equals(moduleAddress)
    .first();
};

export const getNonMigrateCachedItems = async (walletAddress: Hex) => {
  const cachedItems = await db.userCache
    .where('address')
    .equals(walletAddress)
    .filter(
      (item) =>
        item.archVersion !== ARCH_VERSION &&
        !isNullish(item.markedForDeletion) &&
        !item.markedForDeletion
    )
    .sortBy('last_updated');

  return cachedItems.reverse();
};

export const deleteDocItem = async (ddocId: string, walletAddress: string) => {
  await db.userCache
    .where('[address+ddocId]')
    .equals([walletAddress, ddocId])
    .delete();
  await db.ddocs.delete(ddocId);
};
